<script lang="ts">
	import { onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { get } from 'svelte/store';
	import { Container } from '@Shelly/shelly-design-system';
	import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
	import { profile_data } from 'src/stores/account/user';
	import type { chartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
	import { openExternalLink } from 'src/helpers/utilities';
	import { energyPriceApi } from '../../api/verivoxApi';
	import { buildVerivoxRedirectUrl } from '../../api/verivoxRedirect';
	import { energyPriceColorSchemes, type ColorSchemeKey } from '../../utils/colorSchemes';
	import EnergyPriceOptions from '../options/EnergyPriceOptions.svelte';
	import EnergyPriceOptionsSimple from '../options/EnergyPriceOptionsSimple.svelte';
	import EnergyPriceSetupModal from '../../modals/setup/EnergyPriceSetupModal.svelte';
	import VerivoxAttribution from '../../verivox/VerivoxAttribution.svelte';
	import TransitionWrapper from 'src/components/Utility/TransitionWrapper.svelte';
	import { save_profile } from 'src/helpers/profile_helpers';
	import { createFunnel } from 'src/helpers/funnel_tracker';

	const verivoxFunnel = createFunnel('verivox_setup');

	export let chartControlsData: ChartControlsDataInterface;

	const DE_USERS_KEY = 'de_users';
	const VERIVOX_INTEGRATION_KEY = 'verivox_integration';

	let isDismissed = false;
	let setupModalOpen = false;
	let hasCachedLocation = false;
	let cachedPostalCode: string | undefined;
	let cachedLocationId: string | null | undefined;
	let cachedTotalUsage: number = 2500;

	const checkDismissalState = (): boolean => {
		try {
			const profileData = get(profile_data);
			const locationData = profileData?.[DE_USERS_KEY]?.[VERIVOX_INTEGRATION_KEY];

			if (!locationData?.dismissed) {
				return false;
			}

			// Check if dismissal date has passed (if we want time-based dismissal)
			if (locationData.dismissalDate) {
				const now = Date.now();
				// If dismissal was more than 30 days ago, show again
				const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
				if (now - locationData.dismissalDate < thirtyDaysInMs) {
					return true;
				}
				// Dismissal expired, clear it
				clearDismissalState();
				return false;
			}
			return true;
		} catch (error) {
			console.error('Error checking dismissal state:', error);
		}
		return false;
	};

	const handleDismiss = async () => {
		isDismissed = true;
		verivoxFunnel.trackAbandon('banner');
		try {
			const profileData = get(profile_data);
			const locationData = profileData?.[DE_USERS_KEY]?.[VERIVOX_INTEGRATION_KEY];

			if (!locationData) {
				return;
			}

			// Update location data with dismissal flags
			const updatedLocationData = {
				...locationData,
				dismissed: true,
				dismissalDate: Date.now(),
			};

			// Update de_users object with spread operator and save to backend
			const updatedDeUsers = {
				...(profileData[DE_USERS_KEY] || {}),
				[VERIVOX_INTEGRATION_KEY]: updatedLocationData,
			};

			await save_profile(DE_USERS_KEY, updatedDeUsers);
		} catch (error) {
			console.error('Error saving dismissal state:', error);
		}
	};

	const clearDismissalState = async () => {
		try {
			const profileData = get(profile_data);
			const locationData = profileData?.[DE_USERS_KEY]?.[VERIVOX_INTEGRATION_KEY];

			if (!locationData) {
				return;
			}

			// Remove dismissal flags from location data
			const updatedLocationData = { ...locationData };
			delete updatedLocationData.dismissed;
			delete updatedLocationData.dismissalDate;

			// Update de_users object with spread operator and save to backend
			const updatedDeUsers = {
				...(profileData[DE_USERS_KEY] || {}),
				[VERIVOX_INTEGRATION_KEY]: updatedLocationData,
			};

			await save_profile(DE_USERS_KEY, updatedDeUsers);
		} catch (error) {
			console.error('Error clearing dismissal state:', error);
		}
	};

	const handleOpenSetupModal = () => {
		verivoxFunnel.trackStep('setup_started');
		setupModalOpen = true;
	};

	const handleExploreMore = () => {
		if (cachedPostalCode && cachedLocationId) {
			const cachedConsumption = energyPriceApi.getCachedConsumption() || 2500;
			const redirectUrl = buildVerivoxRedirectUrl(
				cachedPostalCode,
				cachedLocationId,
				cachedConsumption,
				'5762',
				'2',
			);
			openExternalLink(redirectUrl);
		} else {
			openExternalLink('https://www.verivox.de');
		}
	};

	// Watch for modal closing and check for cached location
	let wasSetupModalOpen = false;
	$: {
		// Check if modal just closed (was open, now closed)
		if (wasSetupModalOpen && !setupModalOpen) {
			const cached = energyPriceApi.getCachedLocation();
			if (cached) {
				hasCachedLocation = true;
				cachedPostalCode = cached.postalCode;
				cachedLocationId = cached.locationId;
				cachedTotalUsage = energyPriceApi.getCachedConsumption() || 2500;
				// Trigger data fetch by dispatching event
				const event = new CustomEvent('dataReady', { detail: { hasData: false } });
			} else {
				hasCachedLocation = false;
				cachedPostalCode = undefined;
				cachedLocationId = undefined;
				cachedTotalUsage = 2500;
			}
		}
		if (wasSetupModalOpen !== setupModalOpen) {
			wasSetupModalOpen = setupModalOpen;
		}
	}

	let currentScheme: ColorSchemeKey = 'scheme1';

	onMount(() => {
		currentScheme = energyPriceColorSchemes.getCurrentScheme();
		isDismissed = checkDismissalState();
		if (!isDismissed) {
			verivoxFunnel.trackStep('banner');
			const cached = energyPriceApi.getCachedLocation();
			if (cached) {
				hasCachedLocation = true;
				cachedPostalCode = cached.postalCode;
				cachedLocationId = cached.locationId;
				cachedTotalUsage = energyPriceApi.getCachedConsumption() || 2500;
			} else {
				hasCachedLocation = false;
				cachedPostalCode = undefined;
				cachedLocationId = undefined;
				cachedTotalUsage = 2500;
			}
		}
	});
</script>

{#if !isDismissed}
	{#if hasCachedLocation}
		<!-- <TransitionWrapper> -->
		<EnergyPriceOptionsSimple
			variant="light"
			postalCode={cachedPostalCode}
			locationId={cachedLocationId}
			totalUsage={cachedTotalUsage}
			onDismiss={handleDismiss}
		/>

		<!-- </TransitionWrapper> -->

		<!-- {#if hasTariffs}
			<TransitionWrapper>
				<Container class="mt-m" padding="0">
					<div
						class="energy-price-suggestion"
						style="background: {energyPriceColorSchemes.schemes[currentScheme]}"
					>
						<div class="suggestion-header">
							<div class="header-content">
								<div class="suggestion-icon">
									<i class="fas fa-chart-line"></i>
								</div>
								<div class="text-content">
									<h3 class="suggestion-title">
										{$format('alert_heading_energy_price_suggestion', {
											default:
												'Sparen Sie bis zu {savings}% bei den Energiekosten',
											values: { savings: potentialSavings },
											locale: 'de',
										})}
									</h3>
									<p class="suggestion-subtitle">
										{$format('alert_message_energy_price_suggestion_short', {
											default:
												'Vergleichen Sie Energieanbieter basierend auf Ihrem Verbrauch',
											locale: 'de',
										})}
									</p>
								</div>
								<button class="suggestion-cta" type="button" on:click={handleOpenModal}>
									{$format('button_view_offers', {
										default: 'Angebote ansehen',
										locale: 'de',
									})}
									<i class="fas fa-arrow-right"></i>
								</button>
							</div>
						</div>

						<EnergyPriceOptions
							variant="light"
							layout="condensed"
							maxTariffs={5}
							postalCode={cachedPostalCode}
							locationId={cachedLocationId}
							totalUsage={cachedTotalUsage}
						/>

						<VerivoxAttribution />

						<button
							class="dismiss-button"
							type="button"
							aria-label={$format('button_close', { default: 'Close' })}
							on:click={handleDismiss}
						>
							<i class="fas fa-times"></i>
						</button>
					</div>
				</Container>
			</TransitionWrapper>
		{/if} -->
	{:else}
		<TransitionWrapper>
			<Container class="mt-m" padding="0">
				<div
					class="energy-price-suggestion"
					style="background: {energyPriceColorSchemes.schemes[currentScheme]}"
				>
					<div class="suggestion-header">
						<div class="header-content">
							<div class="suggestion-icon">
								<i class="fas fa-bolt"></i>
							</div>
							<div class="text-content">
								<h3 class="suggestion-title">
									{$format('energy_price_setup_prompt_title', {
										default: 'Günstige Stromtarife entdecken',
										locale: 'de',
									})}
								</h3>
								<p class="suggestion-subtitle">
									{$format('energy_price_setup_prompt_message', {
										default:
											'Vergleichen Sie verfügbare Stromtarife in Ihrer Region',
										locale: 'de',
									})}
								</p>
							</div>
							<button class="suggestion-cta" type="button" on:click={handleOpenSetupModal}>
								{$format('button_get_started', {
									default: 'Loslegen',
									locale: 'de',
								})}
								<i class="fas fa-arrow-right"></i>
							</button>
						</div>
					</div>

					<VerivoxAttribution />

					<button
						class="dismiss-button"
						type="button"
						aria-label={$format('button_close', { default: 'Close' })}
						on:click={handleDismiss}
					>
						<i class="fas fa-times"></i>
					</button>
				</div>
			</Container>
		</TransitionWrapper>
	{/if}
{/if}

<EnergyPriceSetupModal bind:showModal={setupModalOpen} />

<!-- Secondary Modal for Energy Price Options -->

<style>
	.energy-price-suggestion {
		border-radius: 12px;
		padding: 16px;
		box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
		position: relative;
		overflow: hidden;
		transition: all 0.3s ease;
	}

	.energy-price-suggestion::before {
		content: '';
		position: absolute;
		top: -50%;
		right: -10%;
		width: 150px;
		height: 150px;
		background: rgba(255, 255, 255, 0.1);
		border-radius: 50%;
		pointer-events: none;
	}

	.suggestion-header {
		position: relative;
		z-index: 1;
		margin-bottom: 12px;
	}

	.header-content {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.suggestion-icon {
		flex-shrink: 0;
		width: 52px;
		height: 52px;
		background: rgba(255, 255, 255, 0.2);
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		color: #ffffff;
		font-size: 26px;
		position: relative;
		z-index: 1;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}

	.text-content {
		flex: 1;
		min-width: 0;
	}

	.suggestion-title {
		font-size: 15px;
		font-weight: 700;
		margin: 0 0 4px 0;
		color: #ffffff;
		line-height: 1.3;
	}

	.suggestion-subtitle {
		font-size: 12px;
		color: rgba(255, 255, 255, 0.9);
		line-height: 1.3;
		margin: 0;
	}

	.suggestion-cta {
		flex-shrink: 0;
		background: rgba(255, 255, 255, 0.95);
		color: #0891b2;
		border: none;
		border-radius: 8px;
		padding: 10px 20px;
		font-size: 13px;
		font-weight: 600;
		cursor: pointer;
		display: flex;
		align-items: center;
		gap: 6px;
		transition: all 0.2s ease;
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
		white-space: nowrap;
	}

	.suggestion-cta:hover {
		background: #ffffff;
		transform: translateY(-1px);
		box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
	}

	.suggestion-cta:active {
		transform: translateY(0);
	}

	.suggestion-cta i {
		font-size: 11px;
		transition: transform 0.2s ease;
	}

	.suggestion-cta:hover i {
		transform: translateX(3px);
	}

	.dismiss-button {
		position: absolute;
		top: 8px;
		right: 8px;
		width: 28px;
		height: 28px;
		background: rgba(255, 255, 255, 0.2);
		border: none;
		border-radius: 6px;
		color: #ffffff;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 14px;
		transition: all 0.2s ease;
		z-index: 2;
		opacity: 0.8;
	}

	.dismiss-button:hover {
		background: rgba(255, 255, 255, 0.3);
		opacity: 1;
	}

	.dismiss-button:active {
		transform: scale(0.95);
	}

	@media (max-width: 768px) {
		.energy-price-suggestion {
			padding: 12px;
		}

		.header-content {
			gap: 10px;
			flex-wrap: wrap;
		}

		.suggestion-icon {
			width: 44px;
			height: 44px;
			font-size: 22px;
		}

		.suggestion-title {
			font-size: 14px;
		}

		.suggestion-subtitle {
			font-size: 11px;
		}

		.suggestion-cta {
			width: 100%;
			justify-content: center;
			padding: 9px 16px;
			font-size: 13px;
			margin-top: 8px;
		}

		.dismiss-button {
			width: 26px;
			height: 26px;
			font-size: 13px;
		}
	}
</style>
